import {
  AfterViewInit,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { RouterOutlet } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';

import { interval, Subscription, timer } from 'rxjs';
import { take } from 'rxjs/operators';

import { BootService } from '@lib-core/boot/services/boot.service';
import { LoaderService } from '@lib-shared/loader/services/loader.service';

import {
  UpdateDialogComponent,
  UpdateDialogResult,
} from '@lib-shared/update/components/update-dialog/update-dialog.component';
import { LayoutCoreSplashScreenComponent } from './lib-core/layout/components/layout-core-splashscreen/layout-core-splashscreen.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [LayoutCoreSplashScreenComponent, RouterOutlet],
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  private dialog: MatDialog = inject(MatDialog);

  private subscriptions: Subscription = new Subscription();
  /**
   * constructor
   *
   */
  constructor(private bootService: BootService) {
    this.bootService.onAppConstruct();
  }

  LoaderService: LoaderService = inject(LoaderService);

  private swUpdate: SwUpdate = inject(SwUpdate);

  /**
   * ngOnInit
   *
   */
  ngOnInit(): void {
    //this.LoaderService.open();
    this.bootService.onAppInit();

    if (this.swUpdate.isEnabled) {
      this.initUpdateMonitoring();
    }
  }

  /**
   * initUpdateMonitoring
   * Monitors for SW updates and handles the update flow to prevent icon flickering
   */
  private initUpdateMonitoring(): void {
    // Periodically check for updates
    this.subscriptions.add(
      interval(1 * 30 * 1000).subscribe(() => {
        this.checkForUpdates();
      }),
    );

    // Handle version updates
    this.subscriptions.add(
      this.swUpdate.versionUpdates.subscribe((event) => {
        if (event.type === 'VERSION_READY') {
          this.handleVersionReady();
        }
      }),
    );

    // Initial check
    this.checkForUpdates();
  }

  /**
   * checkForUpdates
   * Triggers the Service Worker update check
   */
  private checkForUpdates(): void {
    this.swUpdate.checkForUpdate();
  }

  /**
   * handleVersionReady
   * Activates the new SW immediately to prevent mixed state,
   * then prompts user to reload
   */
  private handleVersionReady(): void {
    this.swUpdate.activateUpdate().then(() => {
      this.promptUser();
    });
  }

  /**
   * ngAfterViewInit
   *
   */
  ngAfterViewInit(): void {
    this.bootService.onAppAfterViewInit();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /**
   * promptUser
   * Shows update dialog and reloads on user confirmation
   */
  private promptUser(): void {
    const dialogRef = this.dialog.open(UpdateDialogComponent, {
      disableClose: true,
    });

    this.subscriptions.add(
      dialogRef
        .afterClosed()
        .pipe(take(1))
        .subscribe((result: UpdateDialogResult) => {
        if (result === 'update') {
          window.location.reload();
        } else if (result === 'postpone') {
          this.scheduleUpdatePrompt();
        }
      }),
    );
  }

  private scheduleUpdatePrompt(): void {
    this.subscriptions.add(
      timer(5 * 60 * 1000)
        .pipe(take(1))
        .subscribe(() => {
          this.promptUser();
        }),
    );
  }
}
